import {createPublicClient, createWalletClient, custom, http, type Address, type PublicClient, type WalletClient} from 'viem';
import {arenaAbi, curveAbi, robinhoodChain, type PendingPurchase} from '@colosseum/shared';
import {config} from './config.js';

/** Read client against the configured RPC. Used for quotes and as the fallback when the indexer is down. */
let readClient: PublicClient | null = null;

export function publicClient(): PublicClient {
  readClient ??= createPublicClient({
    chain: robinhoodChain(config.rpcUrl, config.explorerUrl ?? undefined),
    transport: http(config.rpcUrl, {batch: {wait: 16}}),
  });
  return readClient;
}

export function walletClientFor(provider: unknown, account: Address): WalletClient {
  return createWalletClient({
    account,
    chain: robinhoodChain(config.rpcUrl, config.explorerUrl ?? undefined),
    transport: custom(provider as never),
  });
}

/**
 * The contract's own quote, which is what the trade will actually honour. The
 * shared curve mirror gives an instant answer as the user types; this confirms
 * it before anything is signed.
 */
export async function quoteBuyOnChain(curve: Address, ethIn: bigint, buyer: Address | null): Promise<bigint> {
  const client = publicClient();
  // The site always delivers to the buyer, so buyer and recipient are the same
  // address; the creator is exempt from the launch-window tax in either role.
  return buyer
    ? client.readContract({address: curve, abi: curveAbi, functionName: 'quoteBuyFor', args: [ethIn, buyer, buyer]})
    : client.readContract({address: curve, abi: curveAbi, functionName: 'quoteBuy', args: [ethIn]});
}

export async function quoteSellOnChain(curve: Address, tokensIn: bigint): Promise<bigint> {
  return publicClient().readContract({address: curve, abi: curveAbi, functionName: 'quoteSell', args: [tokensIn]});
}

/** The snipe tax this buyer would pay right now, in basis points of the post-fee amount. */
export async function snipeTaxBpsFor(curve: Address, buyer: Address): Promise<bigint> {
  return publicClient().readContract({address: curve, abi: curveAbi, functionName: 'snipeTaxBps', args: [buyer, buyer]});
}

/**
 * The arena's headline numbers straight from the chain, for when the indexer is
 * unreachable. Deliberately minimal: enough for the countdown and the pot, not
 * a reimplementation of the indexer in the browser.
 */
export async function arenaFallback(): Promise<{
  started: boolean; potWei: bigint; nextPeriod: number; nextClose: number; pending: PendingPurchase | null;
} | null> {
  const a = config.addresses?.arena;
  if (!a) return null;
  const client = publicClient();
  try {
    const started = await client.readContract({address: a, abi: arenaAbi, functionName: 'started'});
    const potWei = await client.getBalance({address: a});
    if (!started) return {started: false, potWei, nextPeriod: 0, nextClose: 0, pending: null};
    const nextPeriod = await client.readContract({address: a, abi: arenaAbi, functionName: 'nextPeriod'});
    const nextClose = await client.readContract({
      address: a, abi: arenaAbi, functionName: 'periodClose', args: [nextPeriod],
    });
    let pending: PendingPurchase | null = null;
    if (await client.readContract({address: a, abi: arenaAbi, functionName: 'pending'})) {
      const k = await client.readContract({address: a, abi: arenaAbi, functionName: 'pendingPeriod'});
      const [round, winner, , spent, burned] = await client.readContract({
        address: a, abi: arenaAbi, functionName: 'draws', args: [k],
      });
      pending = {
        period: Number(k), round: round.toString(), winner, symbol: null, logo: null,
        spentWei: spent.toString(), burned: burned.toString(), slices: 0,
      };
    }
    return {started: true, potWei, nextPeriod: Number(nextPeriod), nextClose: Number(nextClose), pending};
  } catch {
    return null;
  }
}
